import React, { useMemo } from "react";
import { Flex, Typography } from "antd";
import { Bar } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Tooltip,
  Legend,
  type ChartData,
  type ChartOptions,
  type Plugin,
} from "chart.js";
import { Card, Skeleton, radius } from "@libertify/ui";
import { analyticsTheme } from "./analyticsTheme";
import {
  getSharedPlugins,
  getCountAxis,
  getCategoryAxis,
  withClippedTicks,
} from "./utils/";

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Tooltip,
  Legend
);

const WHOLE_DATASET_INDEX = 0;
const PART_DATASET_INDEX = 1;
const PART_X_SHIFT_RATIO = 0.5;

// chart.js has no per-dataset x-offset, so shift the part dataset's bars at draw
// time by translating the canvas just for that dataset.
const partOffsetPlugin: Plugin<"bar"> = {
  id: "nestedPartOffset",
  beforeDatasetDraw(chart, args) {
    if (args.index !== PART_DATASET_INDEX) return;
    const wholeBar = chart.getDatasetMeta(WHOLE_DATASET_INDEX).data[0] as
      | { width?: number }
      | undefined;
    const offset = (wholeBar?.width ?? 0) * PART_X_SHIFT_RATIO;
    chart.ctx.save();
    chart.ctx.translate(offset, 0);
  },
  afterDatasetDraw(chart, args) {
    if (args.index !== PART_DATASET_INDEX) return;
    chart.ctx.restore();
  },
};

export interface NestedSeriesConfig {
  label: string;
  data: number[];
  color: string;
}

export interface AnalyticsNestedBarChartProps {
  title: string;
  subtitle?: string;
  labels: string[];
  /** The containing total, drawn full-width behind. */
  wholeSeries: NestedSeriesConfig;
  /** The subset (always ≤ whole), drawn narrower and hatched, in front. */
  partSeries: NestedSeriesConfig;
  loading?: boolean;
  height?: number;
}

const hatchCache = new Map<string, CanvasPattern | string>();
const getHatchPattern = (color: string): CanvasPattern | string => {
  const cached = hatchCache.get(color);
  if (cached) return cached;

  const size = 6;
  const canvas = document.createElement("canvas");
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext("2d");
  if (!ctx) return color;

  ctx.strokeStyle = color;
  ctx.lineWidth = 5;
  ctx.beginPath();
  ctx.moveTo(0, size);
  ctx.lineTo(size, 0);
  ctx.moveTo(-1, 1);
  ctx.lineTo(1, -1);
  ctx.moveTo(size - 1, size + 1);
  ctx.lineTo(size + 1, size - 1);
  ctx.stroke();

  const pattern = ctx.createPattern(canvas, "repeat") ?? color;
  hatchCache.set(color, pattern);
  return pattern;
};

const CARD_STYLE: React.CSSProperties = {
  width: "100%",
  height: "100%",
  borderRadius: radius.box,
  border: `1px solid ${analyticsTheme.cardBorder}`,
  background: analyticsTheme.cardBg,
};

const CARD_BODY_STYLE: React.CSSProperties = {
  display: "flex",
  flexDirection: "column",
  height: "100%",
  padding: "20px 24px",
};

export const AnalyticsNestedBarChart: React.FC<
  AnalyticsNestedBarChartProps
> = ({
  title,
  subtitle,
  labels,
  wholeSeries,
  partSeries,
  loading = false,
  height = 300,
}) => {
  const options = useMemo<ChartOptions<"bar">>(() => {
    const shared = getSharedPlugins();
    return {
      responsive: true,
      maintainAspectRatio: false,
      interaction: { mode: "index", intersect: false },
      plugins: {
        ...shared,
        legend: {
          ...shared.legend,
          labels: {
            ...shared.legend.labels,
            // The part is filled with a canvas pattern; force solid swatches so
            // the legend keys stay legible instead of rendering the hatch.
            generateLabels: (chart) =>
              chart.data.datasets.map((ds, i) => ({
                text: ds.label ?? "",
                fillStyle: i === 0 ? wholeSeries.color : partSeries.color,
                strokeStyle: i === 0 ? wholeSeries.color : partSeries.color,
                lineWidth: 0,
                pointStyle: "rectRounded" as const,
                hidden: !chart.isDatasetVisible(i),
                datasetIndex: i,
              })),
          },
        },
      },
      scales: {
        x: withClippedTicks(getCategoryAxis()),
        y: getCountAxis(),
      },
    };
  }, [wholeSeries.color, partSeries.color]);

  const chartData = useMemo<ChartData<"bar">>(
    () => ({
      labels,
      datasets: [
        {
          label: wholeSeries.label,
          data: wholeSeries.data,
          backgroundColor: wholeSeries.color,
          borderRadius: 4,
          categoryPercentage: 0.7,
          barPercentage: 0.9,
          grouped: false,
          order: 2,
        },
        {
          label: partSeries.label,
          data: partSeries.data,
          backgroundColor: getHatchPattern(partSeries.color),
          borderRadius: 2,
          categoryPercentage: 0.7,
          barPercentage: 0.5,
          grouped: false,
          order: 1,
        },
      ],
    }),
    [labels, wholeSeries, partSeries]
  );

  if (loading) {
    return (
      <Card
        bordered
        shadow="none"
        padding="none"
        style={CARD_STYLE}
        styles={{ body: CARD_BODY_STYLE }}
      >
        <Flex vertical gap={8} style={{ marginBottom: 16 }}>
          <Skeleton variant="rectangular" width="40%" height={18} />
          <Skeleton variant="rectangular" width="55%" height={13} />
        </Flex>
        <Skeleton variant="rectangular" width="100%" height={height} />
      </Card>
    );
  }

  return (
    <Card
      bordered
      shadow="none"
      padding="none"
      style={CARD_STYLE}
      styles={{ body: CARD_BODY_STYLE }}
    >
      <Flex vertical gap={4} style={{ marginBottom: 8 }}>
        <Typography.Text style={{ fontSize: 16, fontWeight: 600 }}>
          {title}
        </Typography.Text>
        {subtitle && (
          <Typography.Text
            style={{
              fontSize: 13,
              fontWeight: 300,
              color: analyticsTheme.textSubtitle,
            }}
          >
            {subtitle}
          </Typography.Text>
        )}
      </Flex>
      <div style={{ flex: 1, maxHeight: height }}>
        <Bar data={chartData} options={options} plugins={[partOffsetPlugin]} />
      </div>
    </Card>
  );
};
